import { toolName } from '@/app/technical';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { failureOf } from '@/api/errors';
import { useAgents, useProspectingPlans, useToolAttachments, useTools } from '@/api/hooks';
import type { Me } from '@/api/session';
import type { WireAgent } from '@/api/wire';
import { Dialog } from '@/ui/Dialog';
import { Select } from '@/ui/fields';
import { Refusal } from '@/ui/Refusal';
import { Button, Field, Skeleton } from '@/ui/primitives';
import { PermissionsStrip, type StripEconomics } from '@/ui/tools';
import { GrantAssignment } from '@/oversight/flows';
import { useDepartmentLabel } from './departments';
import { useFrame } from './frame';

/**
 * The Permissions strip over the page (S:22-permissions-strip.png; Q11,
 * ADR-0134): what the department's agents may use, at the department and
 * plan levels the gateway enforces, read live, with each tool's place in
 * the replace order (ADR-0133). Attaching is a grant, and a grant reaching
 * a vendor's tool is the superadmin's (Q12): the superadmin picks the
 * level here and makes the grant under a token; a lead asks for one.
 */
export function PagePermissions({ me }: { me: Me }) {
  const frame = useFrame();
  const label = useDepartmentLabel();
  const q = useToolAttachments(me.department);
  const agents = useAgents();
  const tools = useTools();
  const [attaching, setAttaching] = useState(false);
  const nameOf = (handle: string) => agents.data?.agents.find((a) => a.workspaceEmail === handle || a.handle === handle)?.givenName ?? handle;
  const economics = new Map<string, StripEconomics>(
    (tools.data?.tools ?? []).filter((t) => t.economics).map((t) => [t.id, { replaceRank: t.economics!.replaceRank, replaceDue: t.economics!.replaceDue ?? null, callsMonth: t.economics!.callsMonth }]),
  );
  return (
    <div className="mx-auto w-full max-w-[1040px] px-6 pt-6 phone:px-4" data-testid="page-permissions">
      {q.isPending && <Skeleton rows={3} />}
      {q.isError && <Refusal failure={failureOf(q.error)} compact />}
      {q.data && (
        <PermissionsStrip
          department={label(me.department)}
          tools={q.data.tools}
          countedFrom={q.data.countedFrom}
          agentName={nameOf}
          economics={economics}
          action={
            me.isSuperadmin ? (
              <Button variant="primary" onClick={() => setAttaching(true)}>
                Attach a tool here
              </Button>
            ) : (
              <Button icon="ask" onClick={() => frame.ask(`Ask the superadmin to attach a tool for ${label(me.department)}, at the department level or for one plan: `)}>Ask for a tool</Button>
            )
          }
        />
      )}
      {attaching && <AttachToolHere me={me} onClose={() => setAttaching(false)} />}
    </div>
  );
}

/**
 * Attach a tool here (F3, ADR-0134): the two levels this page offers. At
 * the department's level, a grant to one of its agents — the §15 flow,
 * under a token bound to the agent. At a plan's level, the plan's own
 * approval is the grant, so the plan is opened rather than a second grant
 * made. A vendor's tool not yet admitted is refused at the door either
 * way, and the dialog says so before the token is minted.
 */
function AttachToolHere({ me, onClose }: { me: Me; onClose: () => void }) {
  const label = useDepartmentLabel();
  const navigate = useNavigate();
  const agents = useAgents();
  const tools = useTools();
  const plans = useProspectingPlans(me.department);
  const [level, setLevel] = useState<'department' | 'plan'>('department');
  const [agentId, setAgentId] = useState('');
  const [toolId, setToolId] = useState('');
  const [granting, setGranting] = useState<WireAgent | null>(null);
  const candidates = (agents.data?.agents ?? []).filter((a) => a.agentKind === 'department' && a.department === me.department && !a.revokedAt);
  const chosenAgent = candidates.find((a) => a.id === agentId) ?? candidates[0] ?? null;
  const held = (tools.data?.tools ?? []).filter((t) => t.departments.includes(me.department));
  const chosenTool = held.find((t) => t.id === toolId) ?? held[0] ?? null;
  const waiting = (plans.data?.plans ?? []).filter((p) => p.state === 'waiting');
  if (granting) return <GrantAssignment agent={granting} presetTools={chosenTool ? [chosenTool.id] : []} presetTitle={`${label(me.department)} · ${chosenTool?.id.split('.').pop()?.replace(/_/g, ' ') ?? 'a tool'}`} onClose={onClose} />;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={`Permissions · ${label(me.department)}`}
      title="Attach a tool here"
      note="Two levels: the department's standing grant to one agent, or a plan's own grant. Both are §15 assignments; the gateway allows a call only under one of them."
      primary={
        level === 'department'
          ? { label: 'Continue to the grant', disabled: !chosenAgent || !chosenTool, onClick: () => chosenAgent && setGranting(chosenAgent) }
          : { label: waiting[0] ? `Open ${waiting[0].segment} · ${waiting[0].territory}` : 'Set up a plan', onClick: () => void navigate(waiting[0] ? `/prospecting/plans/${waiting[0].id}` : '/prospecting/setup') }
      }
      secondary={{ label: 'Cancel', onClick: onClose }}
    >
      <div role="radiogroup" aria-label="Level" className="flex flex-col gap-2">
        {(
          [
            ['department', 'The department', `Every ${label(me.department)} page, for one agent, until a date`],
            ['plan', 'One plan', 'Only while that plan runs, exactly its tools, its ceiling and its budget'],
          ] as const
        ).map(([k, title, hint]) => (
          <label key={k} className="flex min-h-tap items-start gap-2 text-dense">
            <input type="radio" name="attach-level" className="mt-1" checked={level === k} onChange={() => setLevel(k)} />
            <span className="flex flex-col">
              <span>{title}</span>
              <span className="text-label text-meta">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {level === 'department' && (
        <>
          <Field label="The agent">
            <Select aria-label="The agent" value={chosenAgent?.id ?? ''} onChange={(e) => setAgentId(e.target.value)} options={candidates.map((a) => ({ value: a.id, label: a.givenName }))} />
          </Field>
          <Field label="The tool" hint={chosenTool && chosenTool.standing?.admitted === false ? 'Withdrawn: the door refuses a grant of it until it is admitted again.' : chosenTool && !chosenTool.standing && chosenTool.economics?.origin === 'third_party' ? 'A vendor’s tool nobody has admitted: admit it on the Tool library first, or the door refuses the grant.' : 'Only a tool this department’s manifests list.'}>
            <Select aria-label="The tool" value={chosenTool?.id ?? ''} onChange={(e) => setToolId(e.target.value)} options={held.map((t) => ({ value: t.id, label: toolName(t.id) }))} />
          </Field>
          {candidates.length === 0 && <p className="m-0 text-dense text-danger">{label(me.department)} has no agent to grant to.</p>}
        </>
      )}
      {level === 'plan' && (
        <div className="flex flex-col gap-1.5 text-dense">
          {waiting.length === 0 ? (
            <p className="m-0 text-secondary">No plan waits for approval. A plan's tools are set when it is set up, and granted when the superadmin approves it.</p>
          ) : (
            waiting.map((p) => (
              <Link key={p.id} className="text-accent underline" to={`/prospecting/plans/${p.id}`}>
                {p.segment} · {p.territory} — {p.tools.map(toolName).join(', ') || 'no tool'}
              </Link>
            ))
          )}
        </div>
      )}
    </Dialog>
  );
}
